import type { WorkspaceRiskCatalogItem, WorkspaceRiskListItem } from "@unbox/api";
import { SAFETY_POLICY_LABELS } from "@unbox/api";
import { buildKeywordGroups } from "@/components/safety-config/keyword-config";
import type { RiskState } from "@/components/safety-config/RiskRow";
import type { RiskGroup, RiskItem } from "@/components/safety-config/risk-groups";
import { SEVERITY_LABEL } from "@/components/safety-config/risk-groups";

export const WORKSPACE_RISKS_CSV_HEADERS = [
  "riskName",
  "onOff",
  "grouping",
  "description",
  "defaultSeverity",
  "whenDetected",
  "ifConfirmed",
  "keywords",
  "ownBrands",
  "licensedCollabs",
  "competitorsToFlag",
] as const;

export const WORKSPACE_RISKS_CSV_PRETTY_HEADERS = [
  "Risk name",
  "On/Off",
  "Grouping",
  "Description",
  "Default severity",
  "When risk is detected",
  "If risk is confirmed",
  "Keywords",
  "Owned or associated brand logos which should not be flagged *",
  "Brands you're collaborating with that should not be flagged *",
  "Competitors to flag *",
];

export type WorkspaceRiskCsvRow = {
  riskName: string;
  onOff: string;
  grouping: string;
  description: string;
  defaultSeverity: string;
  whenDetected: string;
  ifConfirmed: string;
  keywords: string[];
  ownBrands: string[];
  licensedCollabs: string[];
  competitorsToFlag: string[];
};

const OWN_BRANDS_KEY = "own_brands";
const LICENSED_COLLABS_KEY = "licensed_collabs";
const COMPETITORS_TO_FLAG_KEY = "competitors_to_flag";

const slugifyWorkspaceName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]+/g, "")
    .replace(/[\s-]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const workspaceRisksCsvFilename = (workspaceName: string, date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${slugifyWorkspaceName(workspaceName)}_workspace-risks_${year}_${month}_${day}.csv`;
};

export const buildWorkspaceRiskState = (
  workspaceRisksList: WorkspaceRiskListItem[],
  domainKnowledge: Record<string, string>,
): ((item: RiskItem) => RiskState) => {
  const riskById = new Map(workspaceRisksList.map((r) => [r.riskId, r]));

  return (item: RiskItem): RiskState => {
    const keys = item.keys ?? [item.key];
    const enabledRisk = keys.map((k) => riskById.get(k)).find(Boolean);
    const effectiveConfig = { ...item.config, ...((enabledRisk?.config as Record<string, unknown>) ?? {}) };

    return {
      // Core Safety ("baseline") is always on and can never be disabled (UNB-2098),
      // so the workspace default template shows it on even before a row is stored —
      // matching the merchant, where these risks are seeded on.
      enabled: item.category === "baseline" || keys.some((k) => riskById.has(k)),
      autoConfirm: enabledRisk?.autoConfirm ?? false,
      policy: enabledRisk?.safetyPolicyType ? (SAFETY_POLICY_LABELS[enabledRisk.safetyPolicyType] ?? null) : null,
      severity: item.defaultSeverity,
      keywords: buildKeywordGroups(item.modelType, effectiveConfig, domainKnowledge),
      customCreatorRiskExplanation: null,
    };
  };
};

export const buildWorkspaceRisksCsvRows = (
  groups: RiskGroup[],
  catalog: WorkspaceRiskCatalogItem[],
  workspaceRisksList: WorkspaceRiskListItem[],
  domainKnowledge: Record<string, string>,
): WorkspaceRiskCsvRow[] => {
  const getRiskState = buildWorkspaceRiskState(workspaceRisksList, domainKnowledge);
  const catalogById = new Map(catalog.map((risk) => [risk.id, risk]));
  const storedById = new Map(workspaceRisksList.map((r) => [r.riskId, r]));

  return groups.flatMap((group) =>
    group.items.map((item) => {
      const state = getRiskState(item);
      const keys = item.keys ?? [item.key];
      const stored = keys.map((k) => storedById.get(k)).find(Boolean);
      const catalogRisk = keys.map((k) => catalogById.get(k)).find(Boolean);

      const autoConfirm = stored ? stored.autoConfirm : (catalogRisk?.defaultAutoConfirm ?? false);
      const policyType = stored ? stored.safetyPolicyType : catalogRisk?.defaultSafetyPolicyType;

      const valuesFor = (key: string) => state.keywords.find((g) => g.key === key)?.values ?? [];

      return {
        riskName: item.name,
        onOff: state.enabled ? "On" : "Off",
        grouping: group.name,
        description: item.desc,
        defaultSeverity: SEVERITY_LABEL[state.severity],
        whenDetected: autoConfirm ? "Auto-approve" : "Require review",
        ifConfirmed: policyType ? (SAFETY_POLICY_LABELS[policyType] ?? "") : "",
        keywords: state.keywords.filter((g) => g.source === "config").flatMap((g) => g.values),
        ownBrands: valuesFor(OWN_BRANDS_KEY),
        licensedCollabs: valuesFor(LICENSED_COLLABS_KEY),
        competitorsToFlag: valuesFor(COMPETITORS_TO_FLAG_KEY),
      };
    }),
  );
};
